import { getBurokAppSupabase, isBurokAppSupabaseConfigured } from './supabaseBurokApp';
import {
  generateBurokAppLinkNativeDeeplink,
  generateBurokAppPathNativeDeeplink,
} from './burokLinkDeeplink';
import { isKnownAppRoutePath, isShortLinkHostname } from './burokHosts';

export type ResolvedOpenTarget = {
  /** URL à ouvrir (custom scheme ou https). */
  openUrl: string;
  /** Si set, redirection web préférée (ex. pay.burok.app). */
  webRedirect?: string;
  /** true si trouvé via RPC. */
  resolved: boolean;
  error?: string;
};

/**
 * RPC publique `get_public_short_link` (BUROK APP).
 */
export async function fetchPublicShortLinkTarget(
  code: string
): Promise<{ success: boolean; target_url?: string; error?: string }> {
  const normalized = code.trim();
  if (normalized.length < 2) {
    return { success: false, error: 'invalid_code' };
  }

  if (!isBurokAppSupabaseConfigured()) {
    return { success: false, error: 'supabase_not_configured' };
  }

  const { data, error } = await getBurokAppSupabase().rpc('get_public_short_link', {
    p_code: normalized,
  });

  if (error) {
    return { success: false, error: error.message };
  }

  const row = data as { success?: boolean; target_url?: string; error?: string } | null;
  if (!row?.success || !row.target_url) {
    return { success: false, error: row?.error || 'not_found' };
  }

  return { success: true, target_url: row.target_url.trim() };
}

/** Convertit une target_url stockée en URL d’ouverture web/native. */
export function targetUrlToOpenTarget(targetUrl: string, fallbackCode?: string): ResolvedOpenTarget {
  try {
    const u = new URL(targetUrl);
    const host = u.hostname.toLowerCase();

    // Paiement → laisser le navigateur (pay.burok.app / paylink)
    if (
      host === 'pay.burok.app' ||
      host.endsWith('.paylink.cool') ||
      host === 'paylink.cool' ||
      host === 'paylink.international'
    ) {
      return { openUrl: targetUrl, webRedirect: targetUrl, resolved: true };
    }

    // Canonique app → scheme natif burokapp://path
    if (host === 'app.burok.app' || host === 'www.app.burok.app') {
      return {
        openUrl: generateBurokAppPathNativeDeeplink(u.pathname, u.search),
        resolved: true,
      };
    }

    // Short host avec route d’app connue (ex. link.burok.app/kiosk?…)
    if (isShortLinkHostname(host) && isKnownAppRoutePath(u.pathname)) {
      return {
        openUrl: generateBurokAppPathNativeDeeplink(u.pathname, u.search),
        resolved: true,
      };
    }

    // Autre https → ouvrir tel quel
    if (u.protocol === 'http:' || u.protocol === 'https:') {
      return { openUrl: targetUrl, webRedirect: targetUrl, resolved: true };
    }

    return { openUrl: targetUrl, resolved: true };
  } catch {
    return {
      openUrl: fallbackCode
        ? generateBurokAppLinkNativeDeeplink(fallbackCode)
        : 'burokapp://',
      resolved: false,
      error: 'invalid_target',
    };
  }
}

/** Résout un shortcode → cible d’ouverture. */
export async function resolveShortCodeOpenTarget(code: string): Promise<ResolvedOpenTarget> {
  const result = await fetchPublicShortLinkTarget(code);
  if (!result.success || !result.target_url) {
    return {
      openUrl: generateBurokAppLinkNativeDeeplink(code),
      resolved: false,
      error: result.error,
    };
  }
  return targetUrlToOpenTarget(result.target_url, code);
}
